import React, { useEffect, useState, useCallback, useRef } from "react"
import { X } from "lucide-react"
import GoalPlanningForm, {
  EMPTY_GOAL_PLANNING_FORM,
  mapGoalPlanningToForm,
} from "../../Components/admin/GoalPlanningForm"

const GoalPlanningModal = ({
  isOpen,
  onClose,
  mode = "initiate",
  adminName,
  goalPlanning,
  onInitiate,
  onAdminSubmit,
  onConferenceSubmit,
  loading = false,
}) => {
  const [formData, setFormData] = useState(EMPTY_GOAL_PLANNING_FORM)
  const initializedRef = useRef(false)

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false
      return
    }

    if (!initializedRef.current) {
      if (goalPlanning) {
        setFormData(mapGoalPlanningToForm(goalPlanning))
      } else {
        setFormData(EMPTY_GOAL_PLANNING_FORM)
      }
      initializedRef.current = true
    }
  }, [isOpen, goalPlanning])

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleSubmit = () => {
    if (mode === "initiate") {
      onInitiate?.()
      return
    }

    if (mode === "admin") {
      onAdminSubmit?.(formData)
      return
    }

    if (mode === "super_admin_conference") {
      onConferenceSubmit?.({
        conferenceNotes: formData.conferenceNotes,
        tier2Support: formData.tier2Support,
        conferenceDate: formData.conferenceDate || null,
      })
    }
  }

  const getSubmitLabel = () => {
    if (mode === "initiate") return "Initiate Goal Planning"
    if (mode === "admin") return "Submit to Super Admin"
    if (mode === "super_admin_conference") return "Complete Goal Setting Conference"
    return "Close"
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[95vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-[#241763]">Goal Planning</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Close goal planning modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {mode === "initiate" && (
            <p className="text-sm text-gray-600 mb-4">
              Initiate this goal planning form for{" "}
              <span className="font-semibold">{adminName}</span>. The admin will
              receive it on their account settings dashboard to complete.
            </p>
          )}

          {mode === "admin" && (
            <p className="text-sm text-gray-600 mb-4">
              Complete all sections below and submit. The Goal Setting Conference
              section will be completed by the Super Admin.
            </p>
          )}

          {mode === "super_admin_conference" && (
            <p className="text-sm text-gray-600 mb-4">
              Review the admin&apos;s responses and complete the Goal Setting
              Conference section below.
            </p>
          )}

          <GoalPlanningForm
            formData={formData}
            onChange={handleChange}
            mode={mode}
            adminName={adminName}
          />
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Cancel
          </button>

          {mode !== "view" && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 text-sm bg-[#241763] text-white rounded hover:bg-[#1a1047] disabled:opacity-50"
            >
              {loading ? "Please wait..." : getSubmitLabel()}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default GoalPlanningModal
